/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ICommandBarItemProps, ICommandBarProps, ICommandBarStyles } from '@fluentui/react/lib/CommandBar';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { Entity } from 'src/luis/projects/entities/types/Entity';

export type CompositeEntityChildTableCommandBarProps = TableCommandBarProps<Entity> & {
    /**
     * A list of the entities to display.
     */
    childrenToDisplay: Partial<Entity>[];

    /**
     * The count of the children.
     */
    childrenCount: number;

    /**
     * A callback for adding an entity child.
     */
    onAddChild?: (entity: Partial<Entity>) => boolean | void;

    /**
     * A callback for deleting entity children.
     */
    onDeleteChildren?: () => boolean | void;
};

const messages = defineMessages({
    dropdownPlaceholder: {
        id: 'entities.compositeEntityChildTableCommandBar.dropdownPlaceholder',
        defaultMessage: 'Add new child ...'
    },
    dropdownNoItemsLabel: {
        id: 'entities.compositeEntityChildTableCommandBar.dropdownNoItemsLabel',
        defaultMessage: 'No possible entities to add'
    },
    deleteChildButton: {
        id: 'entities.compositeEntityChildTableCommandBar.deleteChildButton',
        defaultMessage: 'Delete'
    }
});

const { getClassName } = getStylist('CompositeEntityChildTableCommandBar');
const stackWithWidth = getClassName('WideStack', theme => ({ width: theme.dropDownWidth }));
const noPaddingStyles = classNamesFunction<ICommandBarProps, ICommandBarStyles>()({ root: { paddingLeft: 0 } });

/**
 * Renders a command bar for the composite entities children.
 *
 * @param props The input properties for the composite children table.
 */
export const CompositeEntityChildTableCommandBar = (props: CompositeEntityChildTableCommandBarProps) => {
    const { childrenCount, childrenToDisplay, selectedItems, onAddChild: onAddEntityChild } = props;
    const [selectedKey, setSelectedKey] = React.useState<string>('');
    const { localize } = useLocalization();

    const options: IDropdownOption[] = childrenToDisplay.map(c => ({ key: c.id, text: c.name }));

    /**
     * Renders the dropdown used to add entity children.
     */
    const onRenderCreateCommand = () => (
        <Stack verticalAlign="center" className={stackWithWidth}>
            <LuisDropdown
                enableEmptyState
                options={options}
                autoSelect={false}
                onChange={onChange}
                selectedKey={selectedKey}
                placeholder={localize(messages.dropdownPlaceholder)}
                emptyStatePlaceholder={localize(messages.dropdownNoItemsLabel)}
            />
        </Stack>
    );

    /**
     * Resets the dropdown when a child entity is selected
     * for addition. Notifies the parent that an entity
     * was selected.
     *
     * @param option  The dropdown option that was selected.
     */
    const onChange = (_: React.FormEvent<HTMLDivElement>, option: IDropdownOption) => {
        const addedEntity = childrenToDisplay.find(c => c.id === option.key);

        if (onAddEntityChild) {
            onAddEntityChild(addedEntity);
        }

        setSelectedKey('');
    };

    const commands: ICommandBarItemProps[] = [
        { key: 'create', onRender: onRenderCreateCommand },
        {
            key: 'delete',
            name: localize(messages.deleteChildButton),
            iconProps: { iconName: 'Delete' },
            onClick: props.onDeleteChildren,
            'data-automation-id': 'delete'
        }
    ];

    const updatedCommands: ICommandBarItemProps[] = commandBarItemSelectionManager(
        commands,
        ['create'],
        ['create', 'delete'],
        ['create', 'delete'],
        selectedItems.length
    );

    if (selectedItems.length === childrenCount) {
        updatedCommands.find(c => c.key === 'delete').disabled = true;
    }

    return <LuisCommandBar styles={noPaddingStyles} items={updatedCommands} />;
};
